// services/PackageOrderService.js

import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { CURRENT_ACTIVATION_FEE } from "../config/membershipConfig";
import { resolvePackageReferralFields } from "../config/membershipReferralConfig";
import { stripUndefinedForFirestore } from "../utils/stripUndefinedForFirestore";

function resolveLineActivationFee(packageData, actualPackagePrice) {
  if (packageData.isSubItemPackage) {
    return packageData.activationFee ?? CURRENT_ACTIVATION_FEE;
  }
  if (packageData.activationFee != null && packageData.activationFee !== "") {
    return Number(packageData.activationFee);
  }
  if (actualPackagePrice === CURRENT_ACTIVATION_FEE) {
    return CURRENT_ACTIVATION_FEE;
  }
  return null;
}

class PackageOrderService {
  static async createPackageOrder(
    packageData,
    userDetails,
    paymentDetails = {}
  ) {
    try {
      console.log("Creating package order...", packageData, userDetails);

      // Generate order ID
      const timestamp = Date.now();
      const cartOrderId = `CART-${timestamp}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      // UPDATED: Calculate registration fee (same logic as CartPage)
      const calculateRegistrationFee = () => {
        // No registration fee for "Activation Fee" packages
        if (packageData.category === "Activation Fee") {
          return 0;
        }

        // No registration fee if user already purchased packages
        if (userDetails.hasPurchasedPackage) {
          return 0;
        }

        // COMPLETELY REMOVE registration fee - it's no longer charged
        return 0;
      };

      const registrationFee = calculateRegistrationFee();

      // Handle sub-items packages vs regular packages
      const actualPackagePrice = packageData.isSubItemPackage 
        ? packageData.finalTotal 
        : packageData.price;
      
      const isAutoFillUpgrade = packageData.isAutoFillUpgrade === true;
      const { referralShare, profitMargin } = resolvePackageReferralFields(
        packageData,
        actualPackagePrice
      );

      const subItemsData = packageData.isSubItemPackage
        ? {
            subItems: packageData.selectedSubItems || [],
            subItemsTotal: packageData.subItemTotal || 0,
            activationFee:
              packageData.activationFee ?? CURRENT_ACTIVATION_FEE,
            minPackageValue: packageData.totalPackageValue || 0,
          }
        : null;

      const lineActivationFee = resolveLineActivationFee(
        packageData,
        actualPackagePrice
      );
      const packageLine = {
        id: packageData.id,
        name: packageData.name,
        description: packageData.description || "",
        category: packageData.category || "",
        price: actualPackagePrice,
        quantity: 1,
        referralShare,
        profitMargin,
        isSubItemPackage: packageData.isSubItemPackage || false,
        isAutoFillUpgrade,
        subItemsData,
      };
      if (lineActivationFee != null) {
        packageLine.activationFee = lineActivationFee;
      }

      // Create order record for admin to review
      const orderData = {
        cartOrderId,
        orderType: "package_purchase",
        buyerId: userDetails.uid,
        buyerName: userDetails.name || "",
        buyerEmail: userDetails.email || "",

        // Package details
        packages: [packageLine],

        // FIXED: Order totals with correct amounts
        packageTotal: actualPackagePrice,
        registrationFee: registrationFee, // Will be 0
        subtotal: actualPackagePrice + registrationFee,
        grandTotal: actualPackagePrice + registrationFee,
        totalAmount: actualPackagePrice + registrationFee,

        // Additional sub-items package fields
        isSubItemPackage: packageData.isSubItemPackage || false,
        isAutoFillUpgrade,
        subItemsData: subItemsData,

        // Order counts
        orderCounts: {
          totalPackages: 1,
          uniquePackages: 1,
          totalItems: 0,
          uniqueItems: 0,
        },

        // Payment details
        paymentMethod: paymentDetails.method || "manual_transfer",
        paymentDetails: paymentDetails,

        // Status
        orderStatus: "pending", // Confirmed automatically after Cashfree payment
        paymentStatus: "pending",

        // User details
        userDetails: {
          myReferralId: userDetails.myReferralId || null,
          referralIdUsed: userDetails.referralIdUsed || null,
        },

        // Timestamps
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),

        // Additional metadata
        isPackagePurchase: true,
        includesRegistrationFee: registrationFee > 0, // Will be false
      };

      // Add to cartOrders collection (same as regular orders)
      const orderRef = await addDoc(
        collection(db, "cartOrders"),
        stripUndefinedForFirestore(orderData)
      );

      console.log("Package order created successfully:", cartOrderId);
      return { success: true, orderId: cartOrderId, docId: orderRef.id };
    } catch (error) {
      console.error("Error creating package order:", error);
      return { success: false, error: error.message };
    }
  }

  static async updateOrderPaymentDetails(orderId, paymentDetails) {
    try {
      const orderQuery = query(
        collection(db, "cartOrders"),
        where("cartOrderId", "==", orderId)
      );
      const orderSnapshot = await getDocs(orderQuery);

      if (orderSnapshot.empty) {
        throw new Error("Order not found");
      }

      const orderDocRef = doc(db, "cartOrders", orderSnapshot.docs[0].id);
      await updateDoc(orderDocRef, {
        ...paymentDetails,
        paymentMethod: "cashfree",
        paymentStatus: "paid",
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (error) {
      console.error("Error updating order payment details:", error);
      return { success: false, error: error.message };
    }
  }

  static async updateUserPendingPurchase(userId, packageData, orderId) {
    try {
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
        await updateDoc(userDocRef, {
          // Don't set hasPurchasedPackage = true here, let admin do it
          lastPackagePurchaseAttempt: new Date().toISOString(),
          pendingPackagePurchase: {
            packageId: packageData.id,
            packageName: packageData.name,
            orderId: orderId,
            amount: packageData.isSubItemPackage
              ? packageData.finalTotal
              : packageData.price,
            isSubItemPackage: packageData.isSubItemPackage || false,
            isAutoFillUpgrade: packageData.isAutoFillUpgrade || false,
            activationFee: packageData.isSubItemPackage
              ? packageData.activationFee ?? CURRENT_ACTIVATION_FEE
              : null,
            createdAt: new Date().toISOString(),
          },
          updatedAt: new Date().toISOString(),
        });
      }

      return { success: true };
    } catch (error) {
      console.error("Error updating user pending purchase:", error);
      return { success: false, error: error.message };
    }
  }
}

export default PackageOrderService;
